import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { WSMessage, WSConnectionState } from '@services/wsService';
import { wsService } from '@services/wsService';

interface NotificationState {
  // 状态
  messages: WSMessage[];
  unreadCount: number;
  connectionState: WSConnectionState;
  loading: boolean;
  error: string | null;
  
  // 方法
  // 连接管理
  connect: (token: string) => Promise<void>;
  disconnect: () => void;
  
  // 消息管理
  fetchMessages: () => Promise<void>;
  sendMessage: (message: Partial<WSMessage>) => void;
  markAsRead: (messageId: string) => void;
  markAllAsRead: () => void;
  deleteMessage: (messageId: string) => void;
  clearAllMessages: () => void;
  
  // 通知管理
  getUnreadCount: () => number;
  
  // 重置状态
  reset: () => void;
}

export const useNotificationStore = create<NotificationState>()(
  devtools(
    (set, get) => ({
      // 初始状态
      messages: [],
      unreadCount: 0,
      connectionState: 'disconnected',
      loading: false,
      error: null,

      // 连接管理
      connect: async (token) => {
        set({ loading: true, error: null });
        try {
          await wsService.connect(token);
          set({ connectionState: 'connected', loading: false });

          // 订阅 WebSocket 消息
          wsService.subscribe('all', (message) => {
            set((state) => {
              const newMessages = [message, ...state.messages];
              const newUnreadCount = message.read ? state.unreadCount : state.unreadCount + 1;
              return {
                messages: newMessages,
                unreadCount: newUnreadCount,
              };
            });
          });
        } catch (error) {
          // WebSocket 错误已经在 service 中处理，这里只记录连接失败
          set({ loading: false });
          console.warn('WebSocket 连接失败，将在后台重试:', error);
        }
      },

      disconnect: () => {
        wsService.disconnect();
        set({ connectionState: 'disconnected' });
      },

      // 消息管理
      fetchMessages: async () => {
        set({ loading: true, error: null });
        try {
          // TODO: 调用 API 获取历史消息
          // 模拟数据
          const mockMessages: WSMessage[] = [
            {
              id: '1',
              type: 'notification',
              title: '任务分配',
              content: '您被分配了新任务：完成登录页面优化',
              senderId: '2',
              senderName: '管理员',
              taskId: '123',
              createdAt: new Date(Date.now() - 3600000).toISOString(),
              read: false,
            },
            {
              id: '2',
              type: 'project',
              title: '项目更新',
              content: '项目 "管理系统" 已更新截止日期',
              projectId: '456',
              createdAt: new Date(Date.now() - 7200000).toISOString(),
              read: false,
            },
            {
              id: '3',
              type: 'system',
              title: '系统通知',
              content: '系统将于今晚 23:00 进行维护',
              createdAt: new Date(Date.now() - 86400000).toISOString(),
              read: true,
            },
            {
              id: '4',
              type: 'task',
              title: '任务完成',
              content: '任务 "首页布局调整" 已完成',
              taskId: '789',
              createdAt: new Date(Date.now() - 172800000).toISOString(),
              read: true,
            },
          ];
          
          const unreadCount = mockMessages.filter(msg => !msg.read).length;
          set({ messages: mockMessages, unreadCount, loading: false });
        } catch (error) {
          set({ error: '获取消息失败', loading: false });
          console.error('获取消息失败:', error);
        }
      },

      sendMessage: (message) => {
        wsService.send(message);
      },

      markAsRead: (messageId) => {
        set((state) => {
          const message = state.messages.find(msg => msg.id === messageId);
          if (!message || message.read) {
            return state;
          }
          
          const newMessages = state.messages.map(msg => 
            msg.id === messageId ? { ...msg, read: true } : msg
          );
          return {
            messages: newMessages,
            unreadCount: state.unreadCount - 1,
          };
        });
      },

      markAllAsRead: () => {
        set((state) => {
          const newMessages = state.messages.map(msg => ({ ...msg, read: true }));
          return {
            messages: newMessages,
            unreadCount: 0,
          };
        });
      },

      deleteMessage: (messageId) => {
        set((state) => {
          const message = state.messages.find(msg => msg.id === messageId);
          const unreadAdjustment = message && !message.read ? -1 : 0;
          
          return {
            messages: state.messages.filter(msg => msg.id !== messageId),
            unreadCount: Math.max(0, state.unreadCount + unreadAdjustment),
          };
        });
      },

      clearAllMessages: () => {
        set({ messages: [], unreadCount: 0 });
      },

      // 通知管理
      getUnreadCount: () => {
        return get().unreadCount;
      },

      // 重置状态
      reset: () => {
        set({
          messages: [],
          unreadCount: 0,
          connectionState: 'disconnected',
          loading: false,
          error: null,
        });
      },
    })
  )
);